import React from 'react';
import clsx from 'clsx';
import { Navbar } from '@/components/navbar/Navbar/Navbar';
import { Sidebar } from '@/components/sidebar/Sidebar/Sidebar';
import { ResponsivePreviewPanel } from '@/components/widgets/ResponsivePreviewPanel/ResponsivePreviewPanel';
import BuilderCanvas from '@/components/canvas/BuilderCanvas';
import Inspector from '@/components/inspector/index';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectPreviewModeEnabled, selectAnalyticsViewEnabled } from '@/store/system/selectors';
import { setAnalyticsViewEnabled } from '@/store/system/systemSlice';
import type { moveEndFuncType, saveJsonCbType } from './Editor';
import IconLogo from '@/assets/icons/icon-logo.svg?react';
import { CanvasFocusProvider } from '@/context/CanvasFocusContext';
import { AnalyticsPage } from '@/components/analytics/AnalyticsPage/AnalyticsPage';

interface EditorLayoutProps {
  className?: string;
  frameRef: HTMLDivElement | null;
  onNormalizeNode: (normalizeNodeHandler: moveEndFuncType) => void;
  onFrameRef: (el: HTMLDivElement | null) => void;
  onRegisterSaveJson: (saveJsonCb: saveJsonCbType) => void;
  disabled?: boolean;
}

export const EditorLayout: React.FC<EditorLayoutProps> = ({
  className,
  frameRef,
  onNormalizeNode,
  onFrameRef,
  onRegisterSaveJson,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const previewMode = useAppSelector(selectPreviewModeEnabled);
  const analyticsView = useAppSelector(selectAnalyticsViewEnabled);

  // Empty canvas with Leadpages logo for invalid/disabled state
  const DisabledCanvas = () => (
    <div className="relative flex h-[calc(100vh-52px)] flex-grow flex-col items-center justify-center gap-6 bg-neutral-100">
      <IconLogo className="h-20 w-20 text-neutral-300" />
      <div className="flex flex-col items-center gap-2">
        <p className="text-lg font-medium text-neutral-500">Error loading page.</p>
        <p className="text-sm text-neutral-400">Please try again.</p>
      </div>
    </div>
  );

  if (analyticsView) {
    return (
      <AnalyticsPage
        navbar={
          <Navbar
            className="mx-auto w-full"
            variant="analytics"
            onBack={() => dispatch(setAnalyticsViewEnabled(false))}
          />
        }
      />
    );
  }

  return (
    <div
      className={clsx('relative h-[100vh] overflow-hidden bg-neutral-100', className)}
      inert={disabled ? true : undefined}
    >
      <Navbar className="mx-auto w-full" disabled={disabled} />
      <CanvasFocusProvider>
        <div className="flex justify-between">
          <Sidebar className="" disabled={disabled} />
          {disabled ? (
            <DisabledCanvas />
          ) : (
            <BuilderCanvas
              onNormalizeNode={onNormalizeNode}
              onFrameRef={onFrameRef}
              onRegisterSaveJson={onRegisterSaveJson}
            />
          )}
          {!previewMode && !disabled && (
            <>
              <ResponsivePreviewPanel
                className="absolute bottom-5 left-[calc(50%-115px)] z-10"
                frameRef={frameRef}
              />
              <Inspector />
            </>
          )}
        </div>
      </CanvasFocusProvider>
    </div>
  );
};
